import { CheckCircle2, Circle, XCircle } from "lucide-react";
import { Badge, Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  useAdvanceSpvPoolStatus,
  useGetStageGateCheck,
} from "@/hooks/capitalMarkets/useSpvPools";
import type {
  CertificationRecordStatus,
  RequiredCertificationDto,
  SpvPoolDto,
  SpvPoolStatus,
} from "@/types/spvPool";
import { humanizeStatus } from "../../shared/format";

interface AdvanceStatusDialogProps {
  open: boolean;
  onClose: () => void;
  pool: SpvPoolDto;
  targetStatus: SpvPoolStatus;
  /** Invoked when the operator clicks "Open this certification". */
  onOpenCertification?: () => void;
}

const STATUS_BADGE: Record<
  CertificationRecordStatus,
  { status: "finalized" | "archived" | "review" | "error"; label: string }
> = {
  Approved: { status: "finalized", label: "Approved" },
  NotStarted: { status: "archived", label: "Not Started" },
  InProgress: { status: "review", label: "In Progress" },
  Revoked: { status: "error", label: "Revoked" },
};

export function GateStatusChip({ status }: { status: CertificationRecordStatus }) {
  const cfg = STATUS_BADGE[status];
  return <Badge status={cfg.status}>{cfg.label}</Badge>;
}

function StatusIcon({ status }: { status: CertificationRecordStatus }) {
  if (status === "Approved")
    return <CheckCircle2 size={16} className="text-status-success" aria-hidden />;
  if (status === "Revoked")
    return <XCircle size={16} className="text-status-error" aria-hidden />;
  return <Circle size={16} className="text-gray-mid" aria-hidden />;
}

/**
 * SPV Pool v1.0 PR 8 — shared dialog used from both the Overview Quick
 * Actions panel and the Certifications tab's "Advance to …" button. It
 * fetches the stage-gate checklist for the target transition and only
 * enables "Confirm Advance" once every gate is green.
 */
export function AdvanceStatusDialog({
  open,
  onClose,
  pool,
  targetStatus,
  onOpenCertification,
}: AdvanceStatusDialogProps) {
  const toast = useToast();
  const check = useGetStageGateCheck(open ? pool.id : undefined, targetStatus);
  const advance = useAdvanceSpvPoolStatus(pool.id);

  const result = check.data;
  const canAdvance = result?.canAdvance ?? false;
  const gates = result?.required ?? [];

  const handleConfirm = () => {
    advance
      .mutateAsync(targetStatus)
      .then(() => {
        toast.success(`Pool advanced to ${humanizeStatus(targetStatus)}.`);
        onClose();
      })
      .catch((err) => {
        // On a 422 the gate state changed under us — refresh the checklist
        // so the operator sees the live blocking gates.
        toast.error(extractErrorMessage(err));
        check.refetch();
      });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Advance to ${humanizeStatus(targetStatus)}`}
      description={`Every stage-gate certification below must be approved before this pool can move from ${humanizeStatus(
        pool.status,
      )} to ${humanizeStatus(targetStatus)}.`}
      size="md"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!canAdvance || check.isLoading}
            loading={advance.isPending}
            onClick={handleConfirm}
          >
            Confirm Advance
          </Button>
        </>
      }
    >
      {check.isLoading && (
        <p className="text-small text-gray-mid italic m-0">Loading gates…</p>
      )}

      {check.isError && (
        <p className="text-small text-status-error m-0">
          Could not load the stage-gate checklist. Please try again.
        </p>
      )}

      {result && gates.length === 0 && (
        <p className="text-small text-gray-mid m-0" data-testid="advance-no-gates">
          No certifications gate this transition.
        </p>
      )}

      {result && gates.length > 0 && (
        <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
          {gates.map((gate) => (
            <GateRow
              key={gate.certificationDefinitionId}
              gate={gate}
              onOpen={onOpenCertification}
            />
          ))}
        </ul>
      )}
    </Modal>
  );
}

function GateRow({
  gate,
  onOpen,
}: {
  gate: RequiredCertificationDto;
  onOpen?: () => void;
}) {
  return (
    <li
      className="flex items-start gap-space-2 rounded-md border border-border-light p-space-3"
      data-testid="advance-gate-row"
    >
      <span className="mt-[2px]">
        <StatusIcon status={gate.currentStatus} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-space-2">
          <span className="text-small font-bold text-onyx">{gate.name}</span>
          <GateStatusChip status={gate.currentStatus} />
        </div>
        <p className="text-small text-gray-mid mt-space-1 mb-0">
          {gate.description}
        </p>
      </div>
      {onOpen && (
        <button
          type="button"
          className="shrink-0 text-gold-shadow hover:underline text-small"
          onClick={onOpen}
        >
          Open this certification
        </button>
      )}
    </li>
  );
}
